/**
 * Forced or optional employee password change. Uses the employee login card theme.
 */
import React, { useState } from 'react';
import './login.css';
import systemLogo from '../../../assests/icons/system_icon.png';
import Footer from '../common/footer/footer';
import { changeEmployeePassword } from '../../../services/employeeAuthApi';
import {
  getEmployeeEmail,
  markEmployeePasswordChanged,
  notifyEmployeeAuthChanged,
} from '../auth/employeeAuthStorage';

type EmployeeChangePasswordProps = {
  onLogout: () => void;
  onBack?: () => void;
  forced?: boolean;
};

export default function EmployeeChangePassword({
  onLogout,
  onBack,
  forced = false,
}: EmployeeChangePasswordProps) {
  const email = getEmployeeEmail() || '';
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    if (newPassword.length < 8) {
      setError('新しいパスワードは8文字以上にしてください。');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('確認用パスワードが一致しません。');
      return;
    }
    if (newPassword === currentPassword) {
      setError('新しいパスワードは現在のパスワードと異なるものにしてください。');
      return;
    }
    setSubmitting(true);
    try {
      await changeEmployeePassword(currentPassword, newPassword);
      markEmployeePasswordChanged();
      notifyEmployeeAuthChanged();
      onBack?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'パスワードの変更に失敗しました。');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="employee-login">
      <div className="employee-login__card">
        <div className="employee-login__logo-wrap">
          <img
            src={systemLogo}
            alt=""
            className="employee-login__logo"
            aria-hidden
          />
          <h1 className="employee-login__title">書類+</h1>
        </div>
        <hr className="employee-login__divider" />
        <form className="employee-login__form" onSubmit={handleSubmit}>
          <p className="employee-login__hint">
            {forced
              ? '仮パスワードでログインしました。続ける前に、新しいパスワードを設定してください。'
              : '現在のパスワードを確認して、新しいパスワードを設定してください。'}
          </p>
          {email && <p className="employee-login__hint">{email}</p>}
          <input
            type="password"
            className="employee-login__input"
            placeholder="現在のパスワード"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
          <input
            type="password"
            className="employee-login__input"
            placeholder="新しいパスワード（8文字以上）"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
            required
            minLength={8}
          />
          <input
            type="password"
            className="employee-login__input"
            placeholder="新しいパスワード（確認）"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            required
            minLength={8}
          />
          {error && <p className="employee-login__error" role="alert">{error}</p>}
          <button type="submit" className="employee-login__btn" disabled={submitting}>
            {submitting ? '変更中…' : 'パスワードを変更'}
          </button>
          {onBack && !forced && (
            <button
              type="button"
              className="employee-login__btn employee-login__btn--secondary"
              onClick={onBack}
            >
              戻る
            </button>
          )}
          <button
            type="button"
            className="employee-login__link"
            onClick={onLogout}
          >
            ログアウト
          </button>
        </form>
        <Footer layout="horizontal" />
      </div>
    </div>
  );
}
